import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies, movieRegions, movieYears, movieTypes } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";

type SortKey = "latest" | "hot" | "rating";

const CATEGORY_MAP: Record<string, string> = {
  movie: "电影",
  tv: "电视剧",
  all: "全部影视",
};

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新上线" },
  { key: "hot", label: "热度" },
  { key: "rating", label: "评分" },
];

const PAGE_SIZE = 8;

interface FilterGroupProps {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}

function FilterGroup({ label, options, value, onChange }: FilterGroupProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <span className="w-16 shrink-0 pt-1 text-sm font-medium text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {["全部", ...options].map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
              value === option
                ? "bg-primary-500 text-background-50"
                : "bg-background-200 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
            }`}
          >
            {option === "全部" ? `全部${label}` : option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Category() {
  const { type = "all" } = useParams();
  const activeCategory = CATEGORY_MAP[type] ? type : "all";
  const categoryName = CATEGORY_MAP[activeCategory];

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);

  usePageMeta({
    title: `国产影视分类 - ${categoryName} | 国产精品久久`,
    description: `${categoryName}频道汇集国产${categoryName}优质作品，支持按类型、地区、年份筛选与排序，帮助你快速找到想看的国产影视内容。`,
    keywords: `国产${categoryName},${categoryName}推荐,国产影视,影视筛选,国产精品久久`,
  });

  const baseList = useMemo(
    () => (activeCategory === "all" ? movies : movies.filter((m) => m.category === activeCategory)),
    [activeCategory]
  );

  const filtered = useMemo(() => {
    const list = baseList.filter((m) => {
      if (genre !== "全部" && m.type !== genre) return false;
      if (region !== "全部" && m.region !== region) return false;
      if (year !== "全部" && String(m.year) !== year) return false;
      return true;
    });
    const sorted = [...list];
    if (sort === "latest") {
      sorted.sort((a, b) => b.year - a.year || b.rating - a.rating);
    } else if (sort === "rating") {
      sorted.sort((a, b) => b.rating - a.rating);
    } else {
      sorted.sort(
        (a, b) => b.rating * 10 + (b.year - 2000) - (a.rating * 10 + (a.year - 2000))
      );
    }
    return sorted;
  }, [baseList, genre, region, year, sort]);

  const shown = filtered.slice(0, visible);

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setVisible(PAGE_SIZE);
  };

  const updateFilter = (setter: (v: string) => void, value: string) => {
    setter(value);
    setVisible(PAGE_SIZE);
  };

  const availableTypes = useMemo(() => {
    const set = new Set(baseList.map((m) => m.type));
    return movieTypes.filter((t) => set.has(t));
  }, [baseList]);

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{categoryName}</span>
      </nav>

      <header className="mt-4 border-b border-background-200 pb-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {categoryName}
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          共收录 {filtered.length} 部作品，支持按类型、地区、年份筛选与排序
        </p>
      </header>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4 md:p-6">
        <div className="flex items-center justify-between lg:hidden">
          <span className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
            <i className="ri-filter-3-line text-primary-500" />
            筛选条件
          </span>
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground-600"
          >
            {filterOpen ? "收起" : "展开"}
            <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          </button>
        </div>

        <div className={`${filterOpen ? "mt-4 flex" : "hidden"} flex-col gap-4 lg:mt-0 lg:flex`}>
          <FilterGroup label="类型" options={availableTypes} value={genre} onChange={(v) => updateFilter(setGenre, v)} />
          <FilterGroup label="地区" options={movieRegions} value={region} onChange={(v) => updateFilter(setRegion, v)} />
          <FilterGroup label="年份" options={movieYears.map(String)} value={year} onChange={(v) => updateFilter(setYear, v)} />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => {
                setSort(option.key);
                setVisible(PAGE_SIZE);
              }}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                sort === option.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:text-primary-700"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={resetFilters}
          className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-600"
        >
          <i className="ri-refresh-line" />
          重置筛选
        </button>
      </div>

      {shown.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {shown.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      ) : (
        <div className="mt-16 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <p className="mt-4 text-base font-semibold text-foreground-800">没有找到匹配的影视作品</p>
          <p className="mt-2 text-sm text-foreground-500">试试更换筛选条件，或点击下方按钮查看全部内容</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            重置筛选条件
          </button>
        </div>
      )}

      {shown.length > 0 && visible < filtered.length && (
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => setVisible((v) => v + PAGE_SIZE)}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-8 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            加载更多（剩余 {filtered.length - visible} 部）
          </button>
        </div>
      )}
    </div>
  );
}